/**
 * Camera, auto-zoom and focus math. The camera never moves: it sits at the origin looking down -z with a 60 deg
 * vertical FOV, near 0.1, far 100 (ScreenRig.prefab; CAMERA in @nanome-lite/look). Framing is done by moving the
 * scene root (SceneTransform), exactly as SceneAutoZoom.cs / EntryPositioning.cs do.
 */
import { Matrix4, Quaternion, Vector3 } from 'three';
import { CAMERA } from '@nanome-lite/look';
import { domainToGl, type SceneTransform } from './frame';

export interface Bounds {
  /** domain Å */
  min: [number, number, number];
  max: [number, number, number];
  centroid: [number, number, number];
  /** max half-extent from the centroid over the three axes (SceneAutoZoom.cs:110) */
  maxRadius: number;
  count: number;
}

/** Bounds of a subset of a flat xyz array (all atoms when `indices` is undefined). */
export function computeBounds(xyz: Float32Array, indices?: ArrayLike<number>): Bounds {
  const n = indices ? indices.length : xyz.length / 3;
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  let sx = 0, sy = 0, sz = 0;
  for (let k = 0; k < n; k++) {
    const i = (indices ? indices[k]! : k) * 3;
    const x = xyz[i]!, y = xyz[i + 1]!, z = xyz[i + 2]!;
    if (x < min[0]) min[0] = x; if (y < min[1]) min[1] = y; if (z < min[2]) min[2] = z;
    if (x > max[0]) max[0] = x; if (y > max[1]) max[1] = y; if (z > max[2]) max[2] = z;
    sx += x; sy += y; sz += z;
  }
  if (n === 0) return { min: [0, 0, 0], max: [0, 0, 0], centroid: [0, 0, 0], maxRadius: 0, count: 0 };
  const c: [number, number, number] = [sx / n, sy / n, sz / n];
  // SceneAutoZoom.cs:110: max of (avg - min) per axis
  const maxRadius = Math.max(c[0] - min[0], c[1] - min[1], c[2] - min[2]);
  return { min, max, centroid: c, maxRadius, count: n };
}

/** Merge bounds of several atom sets (re-derives the centroid as the count-weighted mean). */
export function mergeBounds(list: Bounds[]): Bounds {
  const valid = list.filter((b) => b.count > 0);
  if (valid.length === 0) return computeBounds(new Float32Array(0));
  if (valid.length === 1) return valid[0]!;
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  let sx = 0, sy = 0, sz = 0, n = 0;
  for (const b of valid) {
    for (let a = 0; a < 3; a++) { if (b.min[a]! < min[a]!) min[a] = b.min[a]!; if (b.max[a]! > max[a]!) max[a] = b.max[a]!; }
    sx += b.centroid[0] * b.count; sy += b.centroid[1] * b.count; sz += b.centroid[2] * b.count; n += b.count;
  }
  const c: [number, number, number] = [sx / n, sy / n, sz / n];
  return { min, max, centroid: c, maxRadius: Math.max(c[0] - min[0], c[1] - min[1], c[2] - min[2]), count: n };
}

/**
 * SceneAutoZoom.CalculateTransform (WorkspaceAPI/src/Application/Scenes/SceneAutoZoom.cs:83-131):
 * scale = clamp(focal * tan(fov/2) * fill / maxRadius, 0.002, 0.2); the centroid sits at the focal point 0.4 m in
 * front of the camera. In GL "in front" is -z, so focalPoint = (0, 0, -0.4) and
 * position = focalPoint - scale * chirality(centroid), rotation identity.
 */
export function autoZoomScale(maxRadius: number): number {
  const { focalMetres, fill, scaleMin, scaleMax } = CAMERA.autoZoom;
  if (!(maxRadius > 0)) return scaleMin * 10; // SceneTransform.InitialTransform scale (0.02)
  const halfFov = (CAMERA.fovDeg * 0.5 * Math.PI) / 180;
  const desiredWorldRadius = focalMetres * Math.tan(halfFov) * fill;
  return Math.min(scaleMax, Math.max(scaleMin, desiredWorldRadius / maxRadius));
}

const _c = new Vector3();
/** Writes the auto-zoom transform for `bounds` into `out` (identity rotation). */
export function autoZoomTransform(bounds: Bounds, out: SceneTransform): SceneTransform {
  const scale = autoZoomScale(bounds.maxRadius);
  domainToGl(bounds.centroid[0], bounds.centroid[1], bounds.centroid[2], _c);
  out.scale = scale;
  out.quaternion.identity();
  out.position.set(-scale * _c.x, -scale * _c.y, -CAMERA.autoZoom.focalMetres - scale * _c.z);
  return out;
}

/**
 * Focus (ZoomOnEntry / ZoomOnComponent): keep the current rotation, move the target centroid to the focal point and
 * scale to fit. Used as the animation end state.
 */
export function focusTransform(bounds: Bounds, current: SceneTransform, out: SceneTransform): SceneTransform {
  const scale = autoZoomScale(bounds.maxRadius);
  domainToGl(bounds.centroid[0], bounds.centroid[1], bounds.centroid[2], _c);
  _c.multiplyScalar(scale).applyQuaternion(current.quaternion);
  out.scale = scale;
  out.quaternion.copy(current.quaternion);
  out.position.set(-_c.x, -_c.y, -CAMERA.autoZoom.focalMetres - _c.z);
  return out;
}

/** Perspective projection (GL convention, column-major) for FOV 60, near 0.1, far 100. */
export function perspective(aspect: number, out = new Matrix4()): Matrix4 {
  const f = 1 / Math.tan((CAMERA.fovDeg * Math.PI) / 360);
  const n = CAMERA.near, fa = CAMERA.far;
  return out.set(
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (fa + n) / (n - fa), (2 * fa * n) / (n - fa),
    0, 0, -1, 0,
  );
}

/**
 * Sub-frustum projection covering the pixel rectangle [x, x+w) x [y, y+h) of a viewport of size (vw, vh), y measured
 * from the bottom (GL). Used for 1x1 picking at the pointer and for tiled snapshots (classic gluPickMatrix).
 */
export function subFrustum(full: Matrix4, x: number, y: number, w: number, h: number, vw: number, vh: number, out = new Matrix4()): Matrix4 {
  const sx = vw / w, sy = vh / h;
  const tx = (vw - 2 * (x + w / 2)) / w;
  const ty = (vh - 2 * (y + h / 2)) / h;
  const pick = new Matrix4().set(
    sx, 0, 0, tx,
    0, sy, 0, ty,
    0, 0, 1, 0,
    0, 0, 0, 1,
  );
  return out.multiplyMatrices(pick, full);
}

/** Project a GL world point to pixel coordinates (origin top-left). Returns null when behind the camera. */
export function projectToScreen(p: Vector3, proj: Matrix4, width: number, height: number): { x: number; y: number; depth: number } | null {
  const e = proj.elements;
  const cx = e[0]! * p.x + e[4]! * p.y + e[8]! * p.z + e[12]!;
  const cy = e[1]! * p.x + e[5]! * p.y + e[9]! * p.z + e[13]!;
  const cz = e[2]! * p.x + e[6]! * p.y + e[10]! * p.z + e[14]!;
  const cw = e[3]! * p.x + e[7]! * p.y + e[11]! * p.z + e[15]!;
  if (cw <= 1e-6) return null;
  return { x: (cx / cw * 0.5 + 0.5) * width, y: (1 - (cy / cw * 0.5 + 0.5)) * height, depth: cz / cw };
}

/** Smoothstep ease used by focus animations (~150 ms). */
export function easeInOut(t: number): number {
  t = Math.min(1, Math.max(0, t));
  return t * t * (3 - 2 * t);
}

const _q = new Quaternion();
/** Interpolate two scene transforms (slerp rotation, log-lerp scale so zoom feels linear). */
export function lerpSceneTransform(a: SceneTransform, b: SceneTransform, t: number, out: SceneTransform): SceneTransform {
  out.position.lerpVectors(a.position, b.position, t);
  out.quaternion.copy(_q.slerpQuaternions(a.quaternion, b.quaternion, t));
  out.scale = Math.exp(Math.log(a.scale) * (1 - t) + Math.log(b.scale) * t);
  return out;
}
